import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAtsPdfGenerator } from "@/hooks/use-ats-pdf-generator";
import { useParallax } from "@/hooks/use-parallax";
import { preloadCriticalImage } from "@/hooks/use-preload-images";
import profileImage from "@/assets/profile.jpg";
import { ProfileImage, HeroContent } from "./hero";

const HeroSection = () => {
  const { t, language } = useLanguage();
  const { generateAtsPdf, isGenerating } = useAtsPdfGenerator();
  
  useEffect(() => {
    preloadCriticalImage(profileImage);
  }, []);

  const slowParallax = useParallax(0.12);
  const mediumParallax = useParallax(0.25);

  const handleDownloadCV = async () => {
    const filename = language === "fr" ? "CV_Aurelia_Ravelo_FR.pdf" : "CV_Aurelia_Ravelo_EN.pdf";
    
    const cvData = {
      language,
      translations: {
        role: t("hero.role"),
        description: t("hero.description"),
        skillsTitle: t("skills.title"),
        experienceTitle: t("experience.title"),
        certificationsTitle: t("certifications.title"),
        educationTitle: t("certifications.education"),
        languagesTitle: t("certifications.languages"),
        skills: [
          { title: t("skills.cloud.title"), description: t("skills.cloud.description") },
          { title: t("skills.systems.title"), description: t("skills.systems.description") },
          { title: t("skills.devops.title"), description: t("skills.devops.description") },
          { title: t("skills.security.title"), description: t("skills.security.description") },
          { title: t("skills.finops.title"), description: t("skills.finops.description") },
          { title: t("skills.migration.title"), description: t("skills.migration.description") },
        ],
        experiences: [
          {
            title: t("experience.job1.title"),
            company: t("experience.job1.company"),
            period: t("experience.job1.period"),
            description: t("experience.job1.description"),
            missions: [
              t("experience.job1.mission1"),
              t("experience.job1.mission2"),
              t("experience.job1.mission3"),
              t("experience.job1.mission4"),
            ],
          },
          {
            title: t("experience.job2.title"),
            company: t("experience.job2.company"),
            period: t("experience.job2.period"),
            description: t("experience.job2.description"),
            missions: [
              t("experience.job2.mission1"),
              t("experience.job2.mission2"),
              t("experience.job2.mission3"),
              t("experience.job2.mission4"),
            ],
          },
          {
            title: t("experience.job3.title"),
            company: t("experience.job3.company"),
            period: t("experience.job3.period"),
            description: t("experience.job3.description"),
            missions: [
              t("experience.job3.mission1"),
              t("experience.job3.mission2"),
              t("experience.job3.mission3"),
              t("experience.job3.mission4"),
            ],
          },
          {
            title: t("experience.job4.title"),
            company: t("experience.job4.company"),
            period: t("experience.job4.period"),
            description: t("experience.job4.description"),
            missions: [
              t("experience.job4.mission1"),
              t("experience.job4.mission2"),
              t("experience.job4.mission3"),
              t("experience.job4.mission4"),
            ],
          },
        ],
        edu1Title: t("certifications.edu1.title"),
        edu1Subtitle: t("certifications.edu1.subtitle"),
        edu1Level: t("certifications.edu1.level"),
        edu2Title: t("certifications.edu2.title"),
        edu2Level: t("certifications.edu2.level"),
        french: t("certifications.french"),
        frenchLevel: t("certifications.french.level"),
        english: t("certifications.english"),
        englishLevel: t("certifications.english.level"),
      },
    };

    await generateAtsPdf(cvData, filename);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">

      {/* Refined background */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/20"
        style={{ transform: `translateY(${slowParallax * 0.3}px)` }}
      />
      
      
      {/* Subtle diagonal lines */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            hsl(var(--primary)) 0px,
            hsl(var(--primary)) 1px,
            transparent 1px,
            transparent 60px
          )`,
        }}
      />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-28 lg:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-10 lg:gap-16 xl:gap-20 max-w-6xl mx-auto">
          {/* Profile Image */}
          <ProfileImage parallaxOffset={-mediumParallax * 0.3} />

          {/* Content */}
          <HeroContent 
            parallaxOffset={-slowParallax * 0.2}
            onDownloadCV={handleDownloadCV}
            isGenerating={isGenerating}
          />
        </div>

        {/* Scroll indicator - refined - hidden on mobile */}
        <div className="hidden sm:block absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 md:w-7 md:h-12 rounded-full border border-primary/20 flex items-start justify-center p-1.5 md:p-2">
            <div className="w-0.5 md:w-1 h-2 md:h-2.5 bg-primary/60 rounded-full animate-glow-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
